import React, { useState, useEffect } from 'react';
import {
  FaUsers,
  FaBuilding,
  FaChartLine,
  FaClock,
  FaCheckCircle,
  FaDatabase,
  FaServer,
  FaUserShield,
  FaUserTie,
  FaUser,
  FaSync,
  FaExclamationTriangle
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import CONFIG from '../../../src/config';
const SuperAdminDashboardContent = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCompanies: 0,
    activeUsers: 0,
    extractionsToday: 0,
    pendingJobs: 0,
    completedJobs: 0,
    failedJobs: 0
  });
  
  const [recentActivities, setRecentActivities] = useState([]);
  const [userDistribution, setUserDistribution] = useState([]);
  const [systemStatus, setSystemStatus] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  

 const getToken = CONFIG.getToken;

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      // Fetch multiple data points in parallel
      const [
  usersResponse,
  companiesResponse,
  jobsResponse,
  userInfoResponse,
] = await Promise.all([
  axios.get(
    CONFIG.getFullUrl(CONFIG.API.USERS.BASE),
    { headers: CONFIG.getAuthHeaders() }
  ),
  axios.get(
    CONFIG.getFullUrl(CONFIG.API.COMPANIES.BASE),
    { headers: CONFIG.getAuthHeaders() }
  ),
  axios.get(
    CONFIG.getFullUrl(CONFIG.API.JOBS.TODAY),
    { headers: CONFIG.getAuthHeaders() }
  ),
  axios.get(
    CONFIG.getFullUrl(CONFIG.API.USERS.ME),
    { headers: CONFIG.getAuthHeaders() }
  ),
]);


      const users = usersResponse.data;
      const companies = companiesResponse.data;
      const jobsData = jobsResponse.data;
      const currentUserData = userInfoResponse.data;

      setCurrentUser(currentUserData);

      // Calculate stats
      const totalUsers = users.length;
      const activeUsers = users.filter(user => user.is_active).length;
      const totalCompanies = companies.length;
      
      // User distribution
      const superadminCount = users.filter(u => u.role === 'superadmin').length;
      const adminCount = users.filter(u => u.role === 'admin').length;
      const userCount = users.filter(u => u.role === 'user').length;
      
      // Calculate percentages
      const total = totalUsers || 1; // Avoid division by zero
      const superadminPercentage = Math.round((superadminCount / total) * 100);
      const adminPercentage = Math.round((adminCount / total) * 100);
      const userPercentage = Math.round((userCount / total) * 100);

      // Update stats
      setStats({
        totalUsers,
        totalCompanies,
        activeUsers,
        extractionsToday: jobsData.total || 0,
        pendingJobs: jobsData.pending || 0,
        completedJobs: jobsData.completed || 0,
        failedJobs: jobsData.failed || 0
      });

      // Update user distribution
      setUserDistribution([
        { 
          role: 'Superusers', 
          count: superadminCount, 
          percentage: superadminPercentage, 
          color: 'bg-purple-600',
          icon: <FaUserShield className="text-purple-600" />
        },
        { 
          role: 'Admins', 
          count: adminCount, 
          percentage: adminPercentage, 
          color: 'bg-blue-600',
          icon: <FaUserTie className="text-blue-600" />
        },
        { 
          role: 'Regular Users', 
          count: userCount, 
          percentage: userPercentage, 
          color: 'bg-green-600',
          icon: <FaUser className="text-green-600" />
        },
      ]);

      // Get recent activities (simulated for now - you can add an actual endpoint)
      const activities = users.slice(0, 5).map(user => ({
        user: user.email.split('@')[0],
        time: formatTimeAgo(new Date(user.created_at)),
        action: getRandomAction(user.role),
        avatarColor: getAvatarColor(user.email)
      }));
      
      setRecentActivities(activities);

      // System status
      setSystemStatus([
        { 
          service: 'API Status', 
          status: 'online', 
          icon: <FaCheckCircle className="text-green-500" />,
          description: 'API server is operational'
        },
        { 
          service: 'Database', 
          status: 'online', 
          icon: <FaDatabase className="text-green-500" />,
          description: 'Connected to PostgreSQL'
        },
        { 
          service: 'Today\'s Jobs', 
          status: `${jobsData.total || 0} total`, 
          icon: <FaServer className="text-blue-500" />,
          description: `${jobsData.completed || 0} completed, ${jobsData.pending || 0} pending`
        },
      ]);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
      
      // Fallback data
      setSystemStatus([
        { 
          service: 'API Status', 
          status: 'error', 
          icon: <FaExclamationTriangle className="text-red-500" />,
          description: 'Failed to connect to API'
        },
        { 
          service: 'Database', 
          status: 'offline', 
          icon: <FaExclamationTriangle className="text-red-500" />,
          description: 'Connection error'
        },
        { 
          service: 'System', 
          status: 'loading...', 
          icon: <FaSync className="animate-spin text-yellow-500" />,
          description: 'Attempting to reconnect'
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  const getRandomAction = (role) => {
    const actions = {
      superadmin: ['System login', 'User management', 'System configuration', 'Audit log view'],
      admin: ['Data extraction', 'Report generation', 'User management', 'File upload'],
      user: ['Report generation', 'Data viewing', 'Profile update', 'File download']
    };
    
    const roleActions = actions[role] || actions.user;
    return roleActions[Math.floor(Math.random() * roleActions.length)];
  };

  const getAvatarColor = (email) => {
    const colors = [
      'from-blue-500 to-cyan-500',
      'from-purple-500 to-pink-500',
      'from-green-500 to-emerald-500',
      'from-orange-500 to-red-500',
      'from-indigo-500 to-purple-500'
    ];
    const index = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  // Initial fetch
  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Stats cards data
  const statCards = [
    { 
      title: 'Total Users', 
      value: stats.totalUsers, 
      icon: <FaUsers />, 
      color: 'bg-blue-500',
      description: `${stats.activeUsers} active users`
    },
    { 
      title: 'Companies', 
      value: stats.totalCompanies, 
      icon: <FaBuilding />, 
      color: 'bg-green-500',
      description: 'Registered in system'
    },
    { 
      title: 'Active Users', 
      value: stats.activeUsers, 
      icon: <FaChartLine />, 
      color: 'bg-purple-500',
      description: `${Math.round((stats.activeUsers / (stats.totalUsers || 1)) * 100)}% of total`
    },
    { 
      title: 'Extractions Today', 
      value: stats.extractionsToday, 
      icon: <FaClock />, 
      color: 'bg-orange-500',
      description: `${stats.completedJobs} completed, ${stats.failedJobs} failed`
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Card */}
      <div className="bg-gradient-to-r from-purple-900 to-indigo-900 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">
              Welcome back, {currentUser?.email?.split('@')[0] || 'Admin'}
            </h2>
            <p className="text-purple-200 mt-1">
              System overview last updated {formatTimeAgo(new Date())}
            </p>
            <div className="mt-4 flex items-center gap-4">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                Role: {currentUser?.role || 'Super Admin'}
              </span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                Users: {stats.totalUsers} total
              </span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                Companies: {stats.totalCompanies} total
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchDashboardData}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              title="Refresh dashboard"
            >
              <FaSync className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <div className="bg-white/10 p-3 rounded-xl">
              <FaUserShield className="text-3xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">{stat.title}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stat.description}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-xl text-white`}>
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
            <button 
              className="text-sm text-purple-600 dark:text-purple-400 hover:underline"
              onClick={fetchDashboardData}
            >
              Refresh
            </button>
          </div>
          <div className="space-y-4">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${activity.avatarColor} flex items-center justify-center text-white text-sm`}>
                      {activity.user.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{activity.user}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{activity.action}</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{activity.time}</span>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No recent activity found
              </div>
            )}
          </div>
        </div>

        {/* User Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">User Distribution</h3>
          <div className="space-y-4">
            {userDistribution.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    {item.icon}
                    <span className="font-medium text-gray-900 dark:text-white">{item.role}</span>
                  </div>
                  <span className="font-semibold">{item.count} users</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className={`${item.color} h-2 rounded-full transition-all duration-500`}
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-1">
                  <span>{item.percentage}% of total</span>
                  <span>{item.count} users</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">System Status</h3>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              Live
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Updated {formatTimeAgo(new Date())}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {systemStatus.map((system, index) => (
            <div key={index} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{system.service}</p>
                  <p className={`text-sm mt-1 ${
                    system.status === 'online' ? 'text-green-600 dark:text-green-400' :
                    system.status === 'error' || system.status === 'offline' ? 'text-red-600 dark:text-red-400' :
                    'text-gray-600 dark:text-gray-400'
                  }`}>
                    {system.status}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {system.description}
                  </p>
                </div>
                <div className="text-2xl">
                  {system.icon}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

    
  
    </div>
  );
};

export default SuperAdminDashboardContent;